import { Star } from 'lucide-react';
import { Avatar, BottomSheet, Button, Textarea } from '@/components/ui';

interface ReviewComposerBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  targetUser: {
    displayName: string;
    iconUrl?: string;
  } | null;
  rating: 1 | 2 | 3 | 4 | 5;
  onRatingChange: (rating: 1 | 2 | 3 | 4 | 5) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  maxCommentLength?: number;
}

export function ReviewComposerBottomSheet({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
  targetUser,
  rating,
  onRatingChange,
  comment,
  onCommentChange,
  maxCommentLength = 500,
}: ReviewComposerBottomSheetProps) {
  const footerContent = (
    <div
      className="flex gap-2 border-t border-neutral-100 bg-white px-4 pt-3"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <Button
        variant="secondary"
        className="flex-1"
        onClick={onClose}
        disabled={isSubmitting}
      >
        キャンセル
      </Button>
      <Button className="flex-1" onClick={onSubmit} isLoading={isSubmitting}>
        投稿する
      </Button>
    </div>
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="レビューを投稿"
      height="auto"
      maxHeight="72dvh"
      footer={footerContent}
    >
      <div className="space-y-4 px-4 pb-4 pt-2">
        {targetUser && (
          <div className="flex items-center gap-3 rounded-xl bg-neutral-50 p-3">
            <Avatar src={targetUser.iconUrl} alt={targetUser.displayName} size="sm" />
            <div>
              <p className="font-medium text-luxury-dark">{targetUser.displayName}</p>
              <p className="text-xs text-neutral-500">1回のみ投稿できます</p>
            </div>
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium text-neutral-700">評価</p>
          <div className="flex items-center gap-2">
            {Array.from({ length: 5 }).map((_, index) => {
              const starValue = (index + 1) as 1 | 2 | 3 | 4 | 5;
              return (
                <button
                  key={starValue}
                  type="button"
                  onClick={() => onRatingChange(starValue)}
                  className="rounded-lg p-1 transition hover:scale-110"
                  aria-label={`${starValue}星`}
                >
                  <Star
                    className={`h-8 w-8 ${
                      starValue <= rating ? 'fill-copper text-copper' : 'text-neutral-300'
                    }`}
                  />
                </button>
              );
            })}
            <span className="ml-2 text-sm text-neutral-500">{rating}.0</span>
          </div>
        </div>

        <Textarea
          label="コメント（任意）"
          placeholder="やり取りの印象を入力してください"
          value={comment}
          onChange={(event) => onCommentChange(event.target.value)}
          rows={4}
          maxLength={maxCommentLength}
          hint={`${comment.length}/${maxCommentLength}`}
        />

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}
      </div>
    </BottomSheet>
  );
}
